import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useCurrency } from '../context/CurrencyContext';
import { CheckCircle2, Copy, Check, Download, ArrowRight, Loader2, Clock, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccess = () => {
  const { orderId } = useParams();
  const { formatPrice } = useCurrency();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [oRes, sRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`),
          axios.get(`${API}/orders/${orderId}/status`),
        ]);
        if (!cancelled) { setOrder(oRes.data); setStatus(sRes.data); }
      } catch (e) {} finally { if (!cancelled) setLoading(false); }
    };
    fetchAll();
    const id = setInterval(fetchAll, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [orderId]);

  const copy = (text, i) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(i);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto p-20 text-center">
      <Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" />
    </div>
  );

  if (!order) return (
    <div className="max-w-3xl mx-auto p-20 text-center">
      <p>Order not found.</p>
      <Link to="/"><Button className="mt-4">Home</Button></Link>
    </div>
  );

  const currentStatus = status?.status || order.status;
  const isPaid = ['paid', 'delivered'].includes(currentStatus);
  const isPending = ['pending_payment', 'confirming'].includes(currentStatus);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-8">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isPaid ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          {isPaid ? <CheckCircle2 className="w-8 h-8 text-emerald-600" /> : <Clock className="w-8 h-8 text-amber-600" />}
        </div>
        <h1 className="font-display text-3xl font-bold">
          {isPaid ? 'Payment received!' : isPending ? 'Waiting for your payment' : 'Order details'}
        </h1>
        <p className="text-neutral-600 mt-2">
          Order <span className="font-mono font-semibold">{order.id}</span> ·
          {isPaid ? ` Codes sent to ${order.email}` : ` We'll email ${order.email} the moment it clears`}
        </p>
      </div>

      {isPending && order.payment_address && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
          <div className="text-sm font-semibold mb-2">Still need to pay?</div>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div className="aspect-square bg-white rounded-xl border p-3 flex items-center justify-center max-w-xs">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(order.payment_address)}&margin=0`} className="w-full h-full" alt="QR" />
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-neutral-500">Amount</div>
                <div className="font-display font-bold text-2xl">{order.payment_amount_crypto} {(order.payment_pay_currency || order.crypto_currency).toUpperCase()}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Address</div>
                <div className="font-mono text-xs bg-neutral-50 border rounded-lg px-3 py-2 break-all">{order.payment_address}</div>
              </div>
              <div className="text-xs text-neutral-500 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5" /> Sending any other coin will result in loss of funds.</div>
            </div>
          </div>
        </div>
      )}

      {isPaid && order.gift_codes && order.gift_codes.length > 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 divide-y">
          {order.gift_codes.map((gc, i) => (
            <div key={i} className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${gc.brand_color || '#8247E5'}15` }}>
                  <span className="font-bold text-xs" style={{ color: gc.brand_color || '#8247E5' }}>{(gc.brand_name || '?').slice(0, 3).toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{gc.brand_name}</div>
                  <div className="text-xs text-neutral-500">{formatPrice(gc.denomination)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-sm bg-neutral-50 border border-dashed border-neutral-300 rounded-lg px-4 py-3 text-center tracking-wider">
                  {gc.code}
                </div>
                <button onClick={() => copy(gc.code, i)} className="h-11 w-11 rounded-lg bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center">
                  {copiedIdx === i ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isPaid && (!order.gift_codes || order.gift_codes.length === 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-sm text-emerald-800">
          Payment received. Your gift codes are being prepared and will arrive in <b>{order.email}</b> within a few minutes.
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <Link to="/browse" className="flex-1">
          <Button className="w-full h-12 bg-[#8247E5] hover:bg-[#6d33cc]">
            Continue shopping <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        <Link to="/dashboard/orders" className="flex-1">
          <Button variant="outline" className="w-full h-12">View all orders</Button>
        </Link>
      </div>
    </div>
  );
};

export default OrderSuccess;
